/* La hoja de una parada: qué es, la reserva si la hay, hecho o saltado, cómo llegar y una nota. */
import { useEffect, useRef, useState } from 'react';
import { anotar, estadoDe, marcaDe, marcar, notaDe, useApuntes, type EstadoParada } from '../datos/apuntes.ts';
import { peligro } from '../datos/retraso.ts';
import type { Dia, Viaje } from '../datos/tipos.ts';
import type { BloqueV } from '../escena/bloques.ts';
import { reducido } from '../escena/motor.ts';
import { Hoja } from '../componentes/Hoja.tsx';
import { CIUDADES, fechaCorta, num2, PRI, titulo } from '../texto.ts';
import { CaraKippu, estadoRes, tieneLoc } from './kippu.tsx';
import { reservasDe } from '../datos/reservas.ts';
import { ListaExamenes } from './Examenes.tsx';
import { FrasesAqui } from '../componentes/Frases.tsx';
import { ParadaCoche } from './Dia.tsx';
import { bloquesDe, type BloqueV as BV } from '../escena/bloques.ts';
import { ahoraMin } from '../tiempo.ts';
import { hhmm } from '../texto.ts';

export function HojaParada({ v, dia, b, esHoy, onCerrada, onFrases }: { v: Viaje; dia: Dia; b: BloqueV; esHoy: boolean; onCerrada: () => void; onFrases: () => void }) {
  useApuntes();
  const estado = estadoDe(b.id);
  const reservas = reservasDe(v, b.id);
  const lugar = b.lugar ? v.lugares.find(l => l.id === b.lugar) : undefined;
  const destino = b.que.split(/ → | \+ | · | \/ /).pop()!;
  const comoLlegar = lugar?.lat != null && lugar.precision !== 'dudosa'
    ? `https://www.google.com/maps/dir/?api=1&destination=${lugar.lat},${lugar.lon}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${destino} ${CIUDADES[dia.ciudad][1]} Japan`)}`;

  const sello = useRef<HTMLSpanElement>(null);
  const golpe = useRef(false);            // solo se estampa con animación al marcarlo, no al abrir
  // Hoy se guarda también el minuto: si se marca «hecho» tarde, la ventanilla avisa de lo que peligra
  const alternar = (e: EstadoParada) => {
    golpe.current = estado !== e && e === 'hecho';
    marcar(b.id, estado === e ? null : e, esHoy ? { min: ahoraMin(), dia: dia.n } : undefined);
  };
  const pel = esHoy ? peligro(bloquesDe(dia), dia.n, marcaDe) : null;
  // En los días de coche, las paradas de su tramo (con «Sitio» y «Ruta»); las opcionales van con la anterior
  const coche = v.coche.dias.find(c => c.dia === dia.n);
  const enTramo = (() => {
    if (!coche) return [];
    let ultima = -1;
    return coche.paradas.map(p => { if (p.ini != null) ultima = p.ini; return { p, ini: p.ini ?? ultima }; })
      .filter(({ ini }) => ini >= b.t.ini && ini < Math.min(b.t.fin, b.t.ini + 300)).map(x => x.p);
  })();

  useEffect(() => {
    const s = sello.current;
    if (!s || !golpe.current || reducido()) return;
    golpe.current = false;
    s.animate([{ transform: 'scale(2.8) rotate(-40deg)', opacity: 0 }, { transform: 'scale(.9) rotate(-14deg)', opacity: 1, offset: 0.55 }, { transform: 'scale(1) rotate(-14deg)', opacity: 1 }], { duration: 440, easing: 'cubic-bezier(.3,1.4,.5,1)', fill: 'forwards' });
    const d = s.closest('dialog')!, c = s.getBoundingClientRect(), o = d.getBoundingClientRect();
    for (let k = 0; k < 10; k++) {
      const p = document.createElement('span'), a = (k / 10) * Math.PI * 2, dist = 50 + Math.random() * 30, sz = 4 + Math.random() * 8;
      p.className = 'salpicadura';
      Object.assign(p.style, { left: `${c.left - o.left + c.width / 2}px`, top: `${c.top - o.top + c.height / 2}px`, width: `${sz}px`, height: `${sz}px` });
      d.append(p);
      p.animate([{ transform: 'translate(-50%,-50%) scale(0)', opacity: 1 }, { transform: `translate(calc(-50% + ${Math.cos(a) * dist}px), calc(-50% + ${Math.sin(a) * dist}px)) scale(1)`, opacity: 0 }], { duration: 520, delay: 200, easing: 'cubic-bezier(.2,.8,.3,1)', fill: 'forwards' }).onfinish = () => p.remove();
    }
    navigator.vibrate?.(18);
  }, [estado]);

  return (
    <Hoja id="hoja-parada" titulo="parada-titulo" onCerrada={onCerrada}>
      {() => (
        <>
          <p className="hoja__kicker">JP{num2(dia.n)} · {b.t.iniTxt} – {b.t.finTxt} · {fechaCorta(dia).toUpperCase()}</p>
          <h2 id="parada-titulo">{titulo(b.que)}</h2>
          <div className="chapas">
            <span className={`chapa chapa--${b.pri}`}>{b.pri} · {PRI[b.pri] ?? ''}</span>
            <span className={`chapa ${/FIJO|CONFIRMADO/.test(b.modo) ? 'chapa--rojo' : /PENDIENTE/.test(b.modo) ? 'chapa--ambar' : ''}`}>{b.modo.toLowerCase()}</span>
            {estado === 'hecho' && <span className="sello-grande" ref={sello} aria-hidden="true">済</span>}
          </div>
          {lugar?.foto && (
            <figure className="foto-sitio">
              <img src={`/api/fotos/${lugar.foto}`} alt={`${lugar.nombre}, foto de Wikipedia`} loading="lazy" decoding="async" />
              <figcaption>{lugar.nombre} · Wikipedia</figcaption>
            </figure>
          )}
          {b.examen ? <ListaExamenes v={v} examenes={[b.examen]} /> : <p className="texto">{b.plan}</p>}
          {pel && pel.ancla.t.ini >= b.t.ini && (
            <div className="aviso aviso--peligro" role="alert">
              <b>▲ Vas {pel.retraso} min tarde.</b> Llegarías a «{pel.ancla.que}» a las {hhmm(pel.llegas)}, {pel.tarde} min después de las {pel.ancla.t.iniTxt}.
              {pel.saltables.length > 0 && <> Si saltas {pel.saltables.map((x: BV) => `«${x.que}» (${x.pri})`).join(' y ')}, llegas a tiempo.</>}
            </div>
          )}
          {reservas.map(res => (
            <div key={res.id}>
              <div className="kippu kippu--plano" data-estado={estadoRes(res)}><CaraKippu v={v} r={res} /></div>
              {tieneLoc(res) && <p className="texto" style={{ fontSize: '.9rem' }}>Localizador <b style={{ fontFamily: 'var(--mono)' }}>{res.localizador}</b></p>}
            </div>
          ))}
          <div className="acciones">
            <button className="accion" type="button" data-real="hecho" aria-pressed={estado === 'hecho'} onClick={() => alternar('hecho')}>✓ Hecho</button>
            <button className="accion" type="button" data-real="saltado" aria-pressed={estado === 'saltado'} onClick={() => alternar('saltado')}>Saltado</button>
            <a className="accion accion--ancha" href={comoLlegar} target="_blank" rel="noopener">Cómo llegar ↗</a>
          </div>
          {lugar?.queEs && (
            <div className="que-es">
              <h3>Qué es</h3>
              <p>{lugar.queEs}</p>
              {lugar.wiki && <a className="enlace-texto" href={lugar.wiki} target="_blank" rel="noopener">Leer más en Wikipedia ↗</a>}
            </div>
          )}
          {estado === 'saltado' && dia.planb && (
            <div className="aviso"><b>Plan B del día.</b> {dia.planb}</div>
          )}
          {enTramo.length > 0 && (
            <>
              <h3>Por el camino · {enTramo.length}</h3>
              {enTramo.map((p, i) => <ParadaCoche key={i} p={p} lugar={v.lugares.find(l => l.id === p.lugar)} />)}
            </>
          )}
          <FrasesAqui v={v} hito={b.hito} tipo={b.tipo} que={b.que} onTodas={onFrases} />
          <label style={{ display: 'grid', gap: '.5rem' }}>
            <h3>Nota del momento</h3>
            <Nota id={b.id} />
          </label>
        </>
      )}
    </Hoja>
  );
}

/** La nota se guarda al dejar de escribir (no a cada tecla) y, si llega cambiada de otro
    dispositivo mientras no se está escribiendo, se ve la nueva. */
function Nota({ id }: { id: string }) {
  const guardada = notaDe(id);
  const [texto, setTexto] = useState(guardada);
  const escribiendo = useRef(false), t = useRef(0);
  useEffect(() => { if (!escribiendo.current) setTexto(guardada); }, [guardada]);
  const guardar = (x: string) => { clearTimeout(t.current); escribiendo.current = false; if (x !== notaDe(id)) anotar(id, x); };
  // Al cerrar la hoja con algo sin guardar, se guarda
  const ultimo = useRef(texto);
  ultimo.current = texto;
  useEffect(() => () => { if (escribiendo.current) guardar(ultimo.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <textarea className="nota" value={texto} placeholder="Qué pasó, qué comiste, qué cambió…"
      onChange={e => { const x = e.target.value; setTexto(x); escribiendo.current = true; clearTimeout(t.current); t.current = window.setTimeout(() => guardar(x), 600); }}
      onBlur={e => guardar(e.target.value)} />
  );
}
